/**
 * 仪表板页面
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { 
  DocumentDuplicateIcon,
  UsersIcon,
  ChartBarIcon,
  TicketIcon,
  PlusIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { quizApi, systemApi } from '@/lib/api';
import { format } from 'date-fns';

export default function Dashboard() {
  // 获取系统信息
  const { data: systemInfo } = useQuery({
    queryKey: ['systemInfo'],
    queryFn: systemApi.getSystemInfo,
    refetchInterval: 30000, // 30秒刷新
  });

  // 获取题集列表
  const { data: quizzesData } = useQuery({
    queryKey: ['quizzes'],
    queryFn: quizApi.getQuizzes,
    refetchInterval: 60000, // 1分钟刷新
  });

  const stats = [
    {
      name: '总题集数',
      value: systemInfo?.total_quizzes || 0,
      icon: DocumentDuplicateIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      href: '/quizzes',
    },
    {
      name: '总用户数',
      value: systemInfo?.total_users || 0,
      icon: UsersIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      href: '/users',
    },
    {
      name: '总答题次数',
      value: systemInfo?.total_attempts || 0,
      icon: ChartBarIcon,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      href: '/attempts',
    },
    {
      name: '可用兑换码',
      value: systemInfo?.available_codes || 0,
      icon: TicketIcon,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      href: '/codes',
    },
  ];

  const recentQuizzes = quizzesData?.quizzes?.slice(0, 5) || [];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">仪表板</h1>
          <p className="mt-2 text-sm text-gray-600">
            管理您的问答应用，查看实时统计信息
          </p>
        </div>
        <Link
          to="/quizzes/create"
          className="btn btn-primary flex items-center gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          创建题集
        </Link>
      </div>

      {/* Statistics cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Link
            key={stat.name}
            to={stat.href}
            className="card hover:shadow-md transition-shadow cursor-pointer group"
          >
            <div className="flex items-center">
              <div className={`flex-shrink-0 p-3 rounded-lg ${stat.bgColor} group-hover:scale-110 transition-transform`}>
                <stat.icon className={`h-6 w-6 ${stat.color}`} />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value.toLocaleString()}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Recent quizzes and quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent quizzes */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">最近的题集</h3>
            <Link
              to="/quizzes"
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
            >
              查看全部
              <EyeIcon className="h-4 w-4" />
            </Link>
          </div>
          
          {recentQuizzes.length > 0 ? (
            <div className="space-y-3">
              {recentQuizzes.map((quiz) => (
                <div
                  key={quiz.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900">{quiz.title}</h4>
                    <p className="text-xs text-gray-600">
                      剩余兑换码: {quiz.remaining_codes || 0}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`
                      inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                      ${new Date() < new Date(quiz.start_at) 
                        ? 'bg-gray-100 text-gray-800' 
                        : new Date() > new Date(quiz.end_at)
                        ? 'bg-red-100 text-red-800'
                        : 'bg-green-100 text-green-800'
                      }
                    `}>
                      {new Date() < new Date(quiz.start_at) 
                        ? '未开始' 
                        : new Date() > new Date(quiz.end_at)
                        ? '已结束'
                        : '进行中'
                      }
                    </span>
                    <Link
                      to={`/quizzes/${quiz.id}/edit`}
                      className="text-primary-600 hover:text-primary-700 text-sm"
                    >
                      编辑
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <DocumentDuplicateIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
              <p>还没有题集，<Link to="/quizzes/create" className="text-primary-600 hover:text-primary-700">立即创建</Link></p>
            </div>
          )}
        </div>

        {/* System info */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">系统信息</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">版本</span>
              <span className="text-sm font-medium text-gray-900">
                {systemInfo?.version || 'v1.0.0'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">环境</span>
              <span className={`
                inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                ${systemInfo?.environment === 'production' 
                  ? 'bg-red-100 text-red-800' 
                  : 'bg-yellow-100 text-yellow-800'
                }
              `}>
                {systemInfo?.environment || 'development'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">数据库状态</span>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {systemInfo?.database_status || '正常'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">最后更新</span>
              <span className="text-sm text-gray-500">
                {format(new Date(), 'yyyy-MM-dd HH:mm')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}